// "Your password" on Your account (#807): changing it while signed in, over
// `POST /auth/password`. The current password is the proof the route asks for; it
// asks for nothing else. A success ends every session, this one too, so the console
// leaves this page for sign-in (`useConsole`'s `changePassword`); a refusal is shown
// here in the server's words, and nothing changed.

import { useState, type FormEvent } from "react";

import { messageOf } from "../api/http";
import { Field, Notice } from "./chrome";

export function YourPassword({
  passwordSet,
  onChange,
}: {
  /** Whether this person has a password at all: one who signs in without has none to change. */
  passwordSet: boolean;
  onChange: (currentPassword: string, newPassword: string) => Promise<void>;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setProblem(null);
    if (next !== again) {
      setProblem("The two new passwords do not match. Nothing was changed.");
      return;
    }
    setBusy(true);
    try {
      await onChange(current, next);
    } catch (failure) {
      setProblem(`${messageOf(failure)} Nothing was changed.`);
      setBusy(false);
    }
  };

  return (
    <section className="panel" id="password" aria-label="Your password">
      <div className="panel__head">
        <h2>Your password</h2>
      </div>
      {passwordSet ? (
        <form className="panel__body stack stack--tight" onSubmit={(event) => void submit(event)}>
          <p className="muted">
            Changing your password signs you out everywhere, this session too. Sign in again
            with the new one.
          </p>
          {problem === null ? null : <Notice tone="bad">{problem}</Notice>}
          <Field
            id="current-password"
            label="Current password"
            type="password"
            autoComplete="current-password"
            required
            disabled={busy}
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
          />
          <Field
            id="new-password"
            label="New password"
            type="password"
            autoComplete="new-password"
            required
            disabled={busy}
            value={next}
            onChange={(event) => setNext(event.target.value)}
          />
          <Field
            id="new-password-again"
            label="New password, again"
            type="password"
            autoComplete="new-password"
            required
            disabled={busy}
            value={again}
            onChange={(event) => setAgain(event.target.value)}
          />
          <div className="actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={busy || current === "" || next === ""}
            >
              {busy ? "Changing…" : "Change password"}
            </button>
          </div>
        </form>
      ) : (
        <div className="panel__body">
          <p className="muted">
            You sign in without a password, so there is no password to change here.
          </p>
        </div>
      )}
    </section>
  );
}
